import React, { useMemo } from 'react';
import Text from '../../../../Components/Base/Text/Text';
import { EventApi } from '@fullcalendar/react';
import CalendarEventTooltipItem from './CalendarEventTooltipItem';
import moment from 'moment';
import useTranslation from '../../../../Hooks/useTranslation';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import {
	EventAttachment,
	EventAttachmentType,
} from '../../../../graphql/graphql';

interface Props {
	event: EventApi;
	eventNote?: string;
	onPreviewUploadedFile: (fileId: string) => void;
}

const CalendarEventTooltip: React.FC<Props> = ({
	event,
	eventNote,
	onPreviewUploadedFile,
}) => {
	const { t } = useTranslation();

	const meta: { key: string; value: string }[] | undefined =
		event.extendedProps.meta;

	const patient = event?.extendedProps?.patient;

	const meetingType = useMemo(() => {
		const type = meta?.find(m => m.key === 'meetingType')?.value;
		if (type === 'onSite') {
			return 'base.meetingType.onSite';
		} else if (type === 'telemedicine') {
			return 'base.meetingType.telemedicine';
		}
		return undefined;
	}, [meta]);

	return (
		<>
			{patient && (
				<>
					<div className={'custom-event__date'}>
						{moment(event.start).format('HH:mm')}
						{event.end && ` - ${moment(event.end).format('HH:mm')}`}
					</div>
					<div className={'custom-event__name'}>
						{event?.extendedProps?.createdByPatient &&
							`${t('component.calendar.thePatientOrderedHimself')}: `}
						{event.title}
					</div>
					{meetingType && (
						<div className={'custom-event__detail__item'}>{t(meetingType)}</div>
					)}
					{event?.extendedProps?.caseType && (
						<div className='custom-event__detail__item'>
							{event.extendedProps.caseType.label}
						</div>
					)}
					<CalendarEventTooltipItem
						label='base.calendar.doctor'
						value={event.extendedProps.doctor.name}
					/>
					<div className={'custom-event__detail__item'}>
						{patient.name && `${patient.name}, `}
						{patient.age && `${patient.age} ${t('base.years.many')}`}
					</div>
					<CalendarEventTooltipItem
						label='base.shared.birthNumberShort'
						value={patient.birthNumber}
					/>
					<CalendarEventTooltipItem
						label='base.phoneShort'
						value={patient.phone}
					/>
					{event.extendedProps.isConfirmedByPatient && (
						<Text
							tx='base.patientConfirmed'
							className='custom-event__detail__item'
						/>
					)}
				</>
			)}
			{event.extendedProps.eventAttachments?.map(
				(attachment: EventAttachment) => {
					if (attachment.type === EventAttachmentType.Upload) {
						return (
							<div className={'custom-event__detail__item'} key={attachment.id}>
								{attachment.title}:{' '}
								<LinkButton
									title='component.calendar.showFile'
									onClick={() => onPreviewUploadedFile(attachment.value.id)}
									theme={Theme.WHITE}
									style={{ fontSize: '12px' }}
								/>
							</div>
						);
					}
					return (
						<div className={'custom-event__detail__item'} key={attachment.id}>
							{attachment.title}: {attachment.value.rawValue}
						</div>
					);
				},
			)}
			<CalendarEventTooltipItem
				label='base.shared.noteShort'
				value={eventNote}
			/>
		</>
	);
};

export default CalendarEventTooltip;
